import {
  Button,
  Grid,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core';
import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import React from 'react';
import { useFormik } from 'formik';
import ConfirmModal from 'components/atoms/ConfirmModal';
import { Autocomplete } from '@material-ui/lab';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';
import ptBR from 'dayjs/locale/pt-br';
import { LABEL_SITUATION_EXIT_SST } from 'constants/stockPosition';
import { ISelectOption } from 'components/molecules/StockPositionGrid/types';
import { useQuery } from 'react-query';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import { postAllOmegaUsers } from 'services/msgraph-users';
import { debounce } from 'lodash';
import * as Yup from 'yup';
import { postExitSST } from 'services/stock-position';
import { IModalExitSSTProps, IOption } from './types';
import useStyles from './style';

const FORM_ID = 'EXIT_SST_FORM_MODAL';

const DEFAULT_HELPER_TEXT = 'Obrigatório';
const NO_OPTIONS_TEXT = 'Nenhum resultado encontrado';
const SEARCH_GUIDE_TEXT = 'Digite 3 Caracteres ou mais para pesquisar';

const ModalExitSST = ({
  open,
  onClose,
  children,
  gridRow,
  reloadData,
}: IModalExitSSTProps) => {
  const classes = useStyles();

  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const [ownerOptions, setOwnerOptions] = React.useState<ISelectOption[]>([]);
  const [fieldsSearchText, setFieldsSearchText] = React.useState<{
    [key: string]: string;
  }>({});

  const validationSchema = Yup.object({
    situation: Yup.string().required(DEFAULT_HELPER_TEXT),
    removalDate: Yup.mixed().required(DEFAULT_HELPER_TEXT),
    owner: Yup.string().when('situation', {
      is: value => value === 'Descarte',
      then: Yup.string().nullable(),
      otherwise: Yup.string().required(DEFAULT_HELPER_TEXT),
    }),
  });

  const formik = useFormik({
    initialValues: {
      situation: '',
      removalDate: null,
      owner: '',
    },
    validationSchema,
    onSubmit: formValues => {
      const stockItemId = gridRow?.id;
      setLoadingSaveForm(true);
      postExitSST({ ...formValues, stockItemId })
        .then(() => {
          if (reloadData) reloadData();
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const helperTextOwner = React.useMemo(() => {
    const extraPropsOwner: { helperText?: string } = {};

    if (formik.values?.situation === 'Descarte') {
      extraPropsOwner.helperText = '';
    }

    return extraPropsOwner;
  }, [formik.values?.situation]);

  const disableOwner = React.useMemo(() => {
    return Boolean(formik.values?.situation === 'Descarte');
  }, [formik.values.situation]);

  const enableQueries = React.useMemo(() => open, [open]);

  const enableOwnerOptions = React.useMemo(() => {
    return (
      enableQueries &&
      Boolean(fieldsSearchText?.owner && fieldsSearchText?.owner.length >= 3)
    );
  }, [enableQueries, fieldsSearchText?.owner]);

  const queryOwnerOptions = useQuery({
    queryKey: ['OwnerOptionsContext', [fieldsSearchText?.owner]],
    queryFn: async () => {
      const params = {
        name: fieldsSearchText?.owner,
      };
      const { data } = await postAllOmegaUsers(params);
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.id,
          value: option.id,
        };
      });
    },
    onSuccess: (data: ISelectOption[]) => {
      setOwnerOptions(data);
    },
    enabled: enableOwnerOptions,
  });

  const situationOptions = Object.values(LABEL_SITUATION_EXIT_SST).map(
    item => ({
      label: item,
      value: item,
    }),
  ) as ISelectOption[];

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const handleAutoCompleteChange = React.useCallback(
    (name: string, option: IOption) => {
      const value = option?.value || '';
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const debounceSetFieldSearchText = React.useMemo(
    () =>
      debounce((name, value) => {
        setFieldsSearchText(currentValues => ({
          ...currentValues,
          [name]: value,
        }));
      }, 600),
    [],
  );

  const handleFieldSearchTextChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (value.length >= 2) {
        debounceSetFieldSearchText(name, value);
      } else {
        debounceSetFieldSearchText(name, '');
      }
    },
    [debounceSetFieldSearchText],
  );

  const onCloseModal = React.useCallback(() => {
    if (formik.dirty) {
      setOpenConfirmModal(true);
    } else {
      onClose();
      formik.resetForm();
    }
  }, [formik, onClose]);

  const getInputValue = (
    inputOptions: IOption[] | undefined,
    selectedValue: string | number | undefined,
  ) => inputOptions?.find(item => item.value === selectedValue)?.label || '';

  const inputRenderer = React.useCallback(
    (params: TextFieldProps, loading?: boolean) => (
      <TextField
        {...params}
        className={classes.formBaseInput}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              <LoadingInputEndAdornment loading={loading} />
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [classes.formBaseInput],
  );

  React.useEffect(() => {
    if (!open) {
      if (formik.dirty) {
        formik.resetForm();
      }

      if (ownerOptions.length > 0) setOwnerOptions([]);
      if (Object.keys(fieldsSearchText).length > 0) setFieldsSearchText({});
    }
  }, [fieldsSearchText, formik, open, ownerOptions.length]);

  const renderBody = React.useCallback(() => {
    return (
      <form
        id={FORM_ID}
        className={classes.bodyContent}
        onSubmit={formik.handleSubmit}
      >
        <Typography className={classes.formMessage}>
          Preencha os campos para efetuar a saída:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Autocomplete
              id="situation"
              options={situationOptions}
              getOptionLabel={option => option.label}
              noOptionsText={NO_OPTIONS_TEXT}
              className={classes.inputList}
              onChange={(e, selected) => {
                formik.setValues(oldValues => ({
                  ...oldValues,
                  owner: '',
                }));

                return handleAutoCompleteChange(
                  'situation',
                  selected as IOption,
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Situação"
                  variant="outlined"
                  helperText="Obrigatório"
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <KeyboardDatePicker
              name="removalDate"
              id="removalDate"
              label="Data Retirada"
              inputVariant="outlined"
              value={formik.values.removalDate}
              onChange={date => {
                handleDateChange('removalDate', date);
              }}
              error={
                formik.touched.removalDate && Boolean(formik.errors.removalDate)
              }
              helperText={
                (formik.touched.removalDate && formik.errors.removalDate) ||
                DEFAULT_HELPER_TEXT
              }
              format="DD/MM/YYYY"
              className={classes.inputList}
              cancelLabel="Cancelar"
              clearLabel="Limpar"
              clearable
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              id="owner"
              options={ownerOptions}
              disabled={disableOwner}
              getOptionLabel={option => option.label}
              noOptionsText={
                !fieldsSearchText?.executorId
                  ? SEARCH_GUIDE_TEXT
                  : NO_OPTIONS_TEXT
              }
              className={classes.inputList}
              inputValue={
                getInputValue(ownerOptions, formik.values?.owner) ||
                formik.values?.owner
              }
              onChange={(e, selected) => {
                return handleAutoCompleteChange('owner', selected as IOption);
              }}
              onInputChange={(e, newValue, reason) => {
                if (reason === 'reset') {
                  formik.setFieldValue('owner', '');
                } else {
                  formik.setFieldValue('owner', newValue);
                }
              }}
              renderInput={params => {
                const extraProps = {
                  name: 'owner',
                  label: 'Proprietário',
                  onChange: handleFieldSearchTextChange,
                  error: formik.touched.owner && Boolean(formik.errors.owner),
                  helperText: DEFAULT_HELPER_TEXT,
                };
                return inputRenderer(
                  { ...params, ...extraProps, ...helperTextOwner },
                  queryOwnerOptions?.isLoading,
                );
              }}
            />
          </Grid>
        </Grid>
      </form>
    );
  }, [
    classes.bodyContent,
    classes.formMessage,
    classes.inputList,
    disableOwner,
    fieldsSearchText?.executorId,
    formik,
    handleAutoCompleteChange,
    handleDateChange,
    handleFieldSearchTextChange,
    helperTextOwner,
    inputRenderer,
    ownerOptions,
    queryOwnerOptions?.isLoading,
    situationOptions,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div>
        <Button color="primary" onClick={onCloseModal}>
          Cancelar
        </Button>
        <ButtonWithLoading
          disabled={!formik.dirty || !formik.isValid || loadingSaveForm}
          form={FORM_ID}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [formik.dirty, formik.isValid, loadingSaveForm, onCloseModal]);

  return (
    <>
      <ConfirmModal
        title="Confirmar Saída"
        message="As alterações feitas não serão salvas."
        open={openConfirmModal}
        width="433px"
        onAccept={() => {
          onClose();
          setOpenConfirmModal(false);
        }}
        onDecline={() => setOpenConfirmModal(false)}
      />
      <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
        <ActionsButtonsBaseModal
          title="Saída SST"
          body={renderBody()}
          footer={renderFooter()}
          open={open}
          onClose={onCloseModal}
          width="516px"
        >
          {children}
        </ActionsButtonsBaseModal>
      </MuiPickersUtilsProvider>
    </>
  );
};

export default ModalExitSST;
